import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import './StudentDashboard.css';

const StudentDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch both profile and dashboard data
            const [profile, dashboard] = await Promise.all([
                authService.getStudentProfile(),
                authService.getStudentDashboard()
            ]);

            setProfileData(profile);
            setDashboardData(dashboard);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message);
            if (error.message.includes('token') || error.message.includes('authentication')) {
                logout();
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="spinner"></div>
                <p>Loading your dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <div className="error-content">
                    <i className="fas fa-exclamation-circle error-icon"></i>
                    <h2>Oops! Something went wrong</h2>
                    <p>{error}</p>
                    <button className="retry-button" onClick={fetchDashboardData}>
                        <i className="fas fa-redo"></i> Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <div className="welcome-section">
                    <h1>Welcome, {profileData?.first_name || 'Student'}!</h1>
                    <p>Here's your learning progress</p>
                </div>

                <div className="quick-stats">
                    <div className="stat-card">
                        <h3>Enrolled Courses</h3>
                        <p>{dashboardData?.stats?.totalCourses || 0}</p>
                    </div>
                    <div className="stat-card">
                        <h3>Completed</h3>
                        <p>{dashboardData?.stats?.completedCourses || 0}</p>
                    </div>
                    <div className="stat-card">
                        <h3>In Progress</h3>
                        <p>{dashboardData?.stats?.inProgressCourses || 0}</p>
                    </div>
                </div>
            </div>

            <div className="dashboard-section">
                <div className="section-header">
                    <h2>My Courses</h2>
                </div>
                <div className="courses-grid">
                    {dashboardData?.courses?.length > 0 ? (
                        dashboardData.courses.map(course => (
                            <div key={course.id} className="course-card">
                                <div className="course-image">
                                    <img src={course.image} alt={course.name} />
                                    {course.completed && (
                                        <span className="completed-badge">
                                            <i className="fas fa-check-circle"></i> Completed
                                        </span>
                                    )}
                                </div>
                                <div className="course-info">
                                    <h3>{course.name}</h3>
                                    <p>{course.description}</p>
                                    <div className="progress-container">
                                        <div className="progress-bar">
                                            <div 
                                                className="progress" 
                                                style={{ width: `${course.progress}%` }}
                                            ></div>
                                        </div>
                                        <span className="progress-text">{course.progress}% Complete</span>
                                    </div>
                                    <button 
                                        className="continue-button"
                                        onClick={() => navigate(`/courses/${course.id}`)}
                                    >
                                        {course.completed ? 'Review Course' : 'Continue Learning'}
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-courses">
                            <img src="/images/empty-courses.svg" alt="No courses" />
                            <h3>No Courses Yet</h3>
                            <p>Start your learning journey by enrolling in a course</p>
                            <button 
                                className="browse-button"
                                onClick={() => navigate('/courses')}
                            >
                                Browse Courses
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Announcements</h2>
                </div>
                <div className="announcements-list">
                    {dashboardData?.announcements?.length > 0 ? (
                        dashboardData.announcements.map(announcement => (
                            <div 
                                key={announcement.id} 
                                className={`announcement-card ${announcement.isRead ? 'read' : 'unread'}`}
                            >
                                <div className="announcement-header">
                                    <h3>{announcement.title}</h3>
                                    <span className="announcement-date">
                                        {new Date(announcement.date).toLocaleDateString()}
                                    </span>
                                </div>
                                <p>{announcement.message}</p>
                            </div>
                        ))
                    ) : (
                        <div className="no-announcements">
                            <p>No announcements at this time</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentDashboard; 